// src/pages/Settings.tsx
import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { useSettingsLocal } from '../hooks/useSettingsLocal';
import { DEFAULT_SETTINGS, mergeWithDefaults } from '../stores/settingsLocal';

type ToggleRowProps = {
  label: string;
  help?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
};

function ToggleRow({ label, help, checked, onChange }: ToggleRowProps) {
  return (
    <div
      className="
        flex flex-col sm:flex-row
        sm:items-center sm:justify-between
        gap-3
        rounded-xl border border-gray-200 dark:border-white/10 p-4
      "
    >
      {/* Texte du label */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-800 dark:text-white break-words">
          {label}
        </p>
        {help && (
          <p className="text-sm text-gray-500 dark:text-gray-400 break-words">
            {help}
          </p>
        )}
      </div>

      {/* Interrupteur */}
      <label className="relative inline-flex cursor-pointer items-center flex-shrink-0 self-end sm:self-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div className="h-6 w-11 rounded-full bg-gray-300 peer-checked:bg-indigo-600 transition-colors"></div>
        <div className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"></div>
      </label>
    </div>
  );
}

export default function Settings() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ⬇️ Source de vérité locale (localStorage + events)
  const { settings, setSettings } = useSettingsLocal();

  // États UI
  const [loading, setLoading] = useState(true);    // chargement initial (hydratation backend)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [savedOk, setSavedOk] = useState(false);

  // On garde un snapshot pour savoir s'il y a des changements locaux non sauvegardés
  const [initial, setInitial] = useState(settings);

  

  // 🔄 Hydratation initiale (optionnelle) depuis le backend
  // - Si la clé existe, on merge et on écrit dans le local store (setSettings).
  // - Si 404 → on garde les DEFAULT_SETTINGS déjà présents dans le store.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await api.get('/settings/ui.dashboard');
        if (cancelled) return;

        const merged = mergeWithDefaults(res.data?.value ?? {});
        setSettings(merged);     // met à jour localStorage + notifie
        setInitial(merged);      // snapshot pour “hasChanges”
      } catch (e: any) {
        if (cancelled) return;
        // 404 = pas encore de settings en base → on utilise ceux du local (probablement defaults)
        if (e?.response?.status === 404) {
          setInitial(settings);
          setError('');
        } else {
          // autre erreur réseau/backend : on affiche un message mais on laisse les settings locaux
          setInitial(settings);
          setError(e?.response?.data?.error || 'Impossible de charger les paramètres');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // volontairement pas “settings” ici

  const hasChanges = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(initial),
    [settings, initial]
  );

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS); // écrit aussi en localStorage
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSavedOk(false);

      await api.put('/settings/ui.dashboard', { value: settings });

      // si réussi, on fige le snapshot
      setInitial(settings);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2000);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Échec de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-svh flex overflow-x-hidden"> {/* ⬅️ bloque tout débordement horizontal */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* colonne contenu */}
      <div className="flex flex-col flex-1 lg:ml-64"> {/* ⬅️ min-w-0 indispensable avec flex */}
        <Header onOpenSidebar={() => setIsSidebarOpen(true)} />

        {/* === MAIN : même recette que ta page OK === */}
        <main className="flex-1 bg-gray-50 dark:bg-darkMode p-4 pt-16 sm:p-6  sm:pt-16 overflow-x-hidden">
          {/* wrapper central : limite la largeur + évite l’overflow */}
          <div className="mx-auto w-full max-w-4xl min-w-0">
            {/* en-tête : même structure que l’autre page */}
            <div className="flex flex-col justify-between gap-4 mb-6">
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Paramètres</h1>
                  <p className="text-gray-500 dark:text-gray-400">
                    Active ou désactive les cartes/graphes visibles sur ton tableau de bord.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {savedOk && (
                    <span className="inline-flex items-center gap-1 rounded-lg bg-green-50 px-2.5 py-1.5 text-sm text-green-700 dark:bg-white/10 dark:text-green-300">
                      <CheckCircleIcon className="h-4 w-4" /> Sauvegardé
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={saving}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5"
                    title="Réinitialiser aux valeurs par défaut"
                  >
                    Réinitialiser
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition ${
                      !hasChanges || saving
                        ? 'bg-gray-300 dark:bg-white/10 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="rounded-xl border border-gray-200 dark:border-white/10 p-6 md:p-2 sm:p-2 text-gray-500 dark:text-gray-400">
                  Chargement des paramètres…
                </div>
              ) : (
                <>
                  {error && (
                    <div className="mb-4 inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-white/10 dark:bg-white/5 dark:text-red-300">
                      <ExclamationTriangleIcon className="h-5 w-5" />
                      {error}
                    </div>
                  )}

                  {/* Section Dashboard */}
                  <section className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 sm:p-6 ">
                    <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90">
                      Tableau de bord — cartes visibles
                    </h2>

                    <div className="space-y-3">
                      <ToggleRow
                        label="Cartes Info Global (CONTRAT / PASSAGE / LISTATT / ZONETECH)"
                        checked={settings.cards.infoGlobal}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, infoGlobal: v } }))
                        }
                      />
                      <ToggleRow
                        label="Places libres"
                        checked={settings.cards.placesLibres}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, placesLibres: v } }))
                        }
                      />
                      <ToggleRow
                        label="Revenus (HT/TTC/Encours/Règlements)"
                        checked={settings.cards.infoRevenu}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, infoRevenu: v } }))
                        }
                      />
                      <ToggleRow
                        label="Encours clients"
                        checked={settings.cards.infoSolde}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, infoSolde: v } }))
                        }
                      />
                      <ToggleRow
                        label="Courbes CA / Règlements (mensuel, cumul)"
                        checked={settings.cards.caMensuelChart}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, caMensuelChart: v } }))
                        }
                      />
                      <ToggleRow
                        label="Carte des nationalités clients"
                        checked={settings.cards.mapNationalites}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, mapNationalites: v } }))
                        }
                      />
                      <ToggleRow
                        label="Répartition bateaux (Contrats/Passages)"
                        checked={settings.cards.nbBateauxChart}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, nbBateauxChart: v } }))
                        }
                      />
                      <ToggleRow
                        label="Places libres par port (barres empilées)"
                        checked={settings.cards.portStackedBars}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, portStackedBars: v } }))
                        }
                      />
                      <ToggleRow
                        label="Familles d’articles (doughnut)"
                        checked={settings.cards.doughnutFamilles}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, doughnutFamilles: v } }))
                        }
                      />
                      <ToggleRow
                        label="Catégories de Bateaux (doughnut)"
                        checked={settings.cards.doughnutCategorieBat}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, doughnutCategorieBat: v } }))
                        }
                      />
                      <ToggleRow
                        label="Types de Bateaux (doughnut)"
                        checked={settings.cards.doughnutTypeBat}
                        onChange={(v) =>
                          setSettings((d) => ({ ...d, cards: { ...d.cards, doughnutTypeBat: v } }))
                        }
                      />
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
